// src/app/features/chat/chatApi.js
import http from "../../api/http.js";

/**
 * TRADER endpoints:
 * GET    /chat/threads/me
 * GET    /chat/messages?threadId=...&cursor=...&limit=...
 * POST   /chat/messages
 * POST   /chat/messages/read
 * POST   /chat/uploads/presign
 *
 * ADMIN endpoints are now in adminApi.js (/features/admin/adminApi.js)
 */

// ===================== Trader API =====================

/**
 * Get the current user's chat thread
 * GET /chat/threads/me
 */
export async function getMyThread() {
  const res = await http.get("/chat/threads/me");
  return res.data; // { threadId, moderatorId }
}

/**
 * List messages for a specific thread
 * GET /chat/messages?threadId=...&cursor=...&limit=...
 */
export async function listMessages({ threadId, cursor = null, limit = 30 }) {
  const res = await http.get("/chat/messages", {
    params: { threadId, cursor, limit },
  });
  return res.data; // { items, nextCursor }
}

/**
 * Send a new message
 * POST /chat/messages
 */
export async function sendMessage(payload) {
  // returns MessageOut
  const res = await http.post("/chat/messages", payload);
  return res.data;
}

/**
 * Mark messages as read in a thread
 * POST /chat/messages/read
 */
export async function markRead({ threadId, lastReadMessageId }) {
  const res = await http.post("/chat/messages/read", { threadId, lastReadMessageId });
  return res.data; // { ok: true }
}

/**
 * Presign flow (required by backend):
 * 1) POST /chat/uploads/presign -> { uploadUrl, s3Key, fileUrl, expiresIn }
 * 2) PUT uploadUrl with the file bytes (no auth header)
 * 3) Send message referencing s3Key (backend blocks raw URLs)
 */
export async function presignUpload({ fileName, mimeType, size }) {
  const res = await http.post("/chat/uploads/presign", { fileName, mimeType, size });
  return res.data; // { uploadUrl, s3Key, fileUrl?, expiresIn }
}

/**
 * Upload file to presigned URL
 * PUT uploadUrl with the file bytes (no auth header)
 */
export async function uploadToPresignedUrl(uploadUrl, file, mimeType) {
  // Do NOT use axios instance with auth headers; presigned URLs must be used as-is.
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": mimeType || file.type || "application/octet-stream",
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Upload failed (${res.status})`);
  }
  return true;
}
